import { Dialog, SelectChangeEvent, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyledMUIButton } from 'src/components/Button';
import { CircleXButton } from 'src/components/Button/CircleXButton';
import { WhiteTextFieldWithoutLabel } from 'src/components/TextInput/WhiteTextFieldWithoutLabel';
import { Toast } from 'src/components/Toast';
import { usePermissions } from 'src/hooks/usePermissions';
import { removeProductFile, updateProductFile } from 'src/services/api';
import { ProductFileData, TenantUploadDBItem } from '../mapSrcToProductFiles';
import {
  CancelButton,
  FileDetailsDialogContent,
  FileDetailsDialogTitle,
  FileDetailsRowContainer,
  FilesListContainer,
  FlexRow,
  HeadersContainer,
  Label,
  LeftColumn,
  OutlinedButton,
  RightColumn,
  RowDivider,
  StyledDialogActions,
  StyledDivider,
  TextFieldContainer,
} from '../styles';
import { FileType, PRODUCT_FILES_QUERY_KEY } from '../Uploader';
import { FileName } from '../UploadModal/FileName';
import { FileTypeRadioGroup } from './FileTypeRadioGroup';

type FileDetailsUploadModalProps = Readonly<{
  isOpen: boolean;
  productFile: ProductFileData;
  onClose: () => void;
}>;

export function FileDetailsEditModal({ isOpen, onClose, productFile }: FileDetailsUploadModalProps) {
  const [editedFile, setEditedFile] = useState<ProductFileData>(productFile);

  const queryClient = useQueryClient();

  const canDeleteProduct = usePermissions(['delete', 'product']);

  const { t } = useTranslation('uploads');

  const encodedUploadId = productFile?.uploadId
    ? encodeURIComponent(productFile.uploadId)
    : /* istanbul ignore next */ '';
  const newDBItem = mapToTenantUploadDBItem(editedFile);
  const updateFileMutationFn = () =>
    editedFile ? updateProductFile(encodedUploadId, newDBItem) : /* istanbul ignore next */ Promise.resolve();

  const mutationFn = () =>
    productFile ? removeProductFile(encodedUploadId) : /* istanbul ignore next */ Promise.resolve();
  const {
    mutate: handleRemove,
    isError: isErrorRemove,
    isLoading: isLoadingRemove,
    isSuccess: isSuccessRemove,
  } = useMutation({
    mutationFn,
    onSuccess() {
      queryClient.invalidateQueries([PRODUCT_FILES_QUERY_KEY]);
      onClose();
    },
  });

  const {
    mutate: handleUpdate,
    isError: isUpdateError,
    isSuccess: isUpdateSuccess,
  } = useMutation({
    mutationFn: updateFileMutationFn,
    onSuccess() {
      queryClient.invalidateQueries([PRODUCT_FILES_QUERY_KEY]);
      onClose();
    },
  });

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDescription = event.target.value;
    setEditedFile(
      (currentFile) =>
        ({
          ...currentFile,
          description: newDescription,
        } as ProductFileData)
    );
  };

  /* istanbul ignore next */
  const handleTypeChange = (type: string) => {
    if (isFileType(type)) {
      setEditedFile(
        (currentFile) =>
          ({
            ...currentFile,
            type,
          } as ProductFileData)
      );
    }
  };

  const productAssigned = editedFile?.products?.[0]?.name ?? /* istanbul ignore next */ t('none');

  return (
    <>
      <Dialog
        fullWidth
        PaperProps={{ style: { overflow: 'visible', maxWidth: 749 } }}
        open={isOpen}
        data-testid="file-edit-details-modal"
      >
        <CircleXButton onClick={onClose} />
        <FileDetailsDialogTitle>{t('editFile')}</FileDetailsDialogTitle>
        <FileDetailsDialogContent>
          <FilesListContainer>
            <HeadersContainer>
              <FlexRow>
                <LeftColumn>
                  <Label>{t('file')}</Label>
                </LeftColumn>
                <RightColumn>
                  <FlexRow sx={{ justifyContent: 'flex-start' }}>
                    <Label>{t('productAssigned')}</Label>
                  </FlexRow>
                </RightColumn>
              </FlexRow>
              <StyledDivider />
            </HeadersContainer>
            <FileDetailsRowContainer>
              <FlexRow>
                <LeftColumn>
                  <FileName fileName={editedFile.name} />
                </LeftColumn>
                <RightColumn>
                  <FlexRow sx={{ justifyContent: 'flex-start' }}>
                    <Typography>{productAssigned}</Typography>
                  </FlexRow>
                </RightColumn>
              </FlexRow>
              <FlexRow>
                <TextFieldContainer>
                  <Label>{t('description')}</Label>
                  <WhiteTextFieldWithoutLabel
                    value={editedFile.description}
                    onChange={handleDescriptionChange}
                    multiline
                    placeholder={`${t('file')} ${t('description').toLowerCase()}`}
                    data-testid="file-edit-description-input"
                  />
                </TextFieldContainer>
              </FlexRow>
              <RowDivider />
            </FileDetailsRowContainer>
            <EditFilePurpose productFile={editedFile} editFileType={handleTypeChange} />
            <RowDivider />
            <FlexRow>
              {canDeleteProduct && (
                <OutlinedButton
                  disabled={isLoadingRemove}
                  onClick={() => handleRemove()}
                  data-testid="file-edit-delete"
                >
                  {t('removeFile')}
                </OutlinedButton>
              )}
            </FlexRow>
          </FilesListContainer>
        </FileDetailsDialogContent>
        <StyledDialogActions>
          <CancelButton onClick={onClose} data-testid="file-edit-cancel">
            {t('discardChanges')}
          </CancelButton>
          <StyledMUIButton onClick={() => handleUpdate()} data-testid="file-edit-save">
            {t('saveChanges')}
          </StyledMUIButton>
        </StyledDialogActions>
      </Dialog>
      <Toast
        dependency={isErrorRemove}
        severity="error"
        title={t('status.error.uploadErrorTitle')}
        message={t('status.error.problemDeletingYourFile', { fileName: editedFile.name })}
      />
      <Toast
        dependency={isSuccessRemove}
        severity="success"
        title={t('status.success.successfullyDeleted')}
        message={t('status.success.fileSuccessfullyDeleted', { fileName: editedFile.name })}
      />
      <Toast
        dependency={isUpdateError}
        severity="error"
        title={t('status.error.uploadErrorTitle')}
        message={t('status.error.problemEditingYourFile', { fileName: editedFile.name })}
      />
      <Toast
        dependency={isUpdateSuccess}
        severity="success"
        title={t('status.success.successfullyUpdated')}
        message={t('status.success.fileSuccessfullyUpdated', { fileName: editedFile.name })}
      />
    </>
  );
}

type EditFilePurposeProps = {
  productFile: ProductFileData;
  editFileType: (type: string) => void;
};

function EditFilePurpose({ productFile, editFileType }: EditFilePurposeProps) {
  const onChange = /* istanbul ignore next */ (event: SelectChangeEvent<unknown>) => {
    editFileType(event.target.value as string);
  };

  const showSelectType = productFile.name.match(/.(xls|xlsx)$/);
  if (!showSelectType) return null;

  return (
    <FileDetailsRowContainer>
      <FlexRow>
        <FileTypeRadioGroup productFile={productFile} onChange={onChange} />
      </FlexRow>
    </FileDetailsRowContainer>
  );
}
/* istanbul ignore next */
function isFileType(type: string): type is FileType {
  const types: (FileType | string)[] = ['bom', 'pricing', 'other'];
  return types.includes(type);
}

function mapToTenantUploadDBItem(productFile?: ProductFileData): Partial<TenantUploadDBItem> {
  /* istanbul ignore next */
  if (!productFile) return {};
  return {
    description: productFile.description ?? '',
  };
}
